import {LitElement, html} from 'lit';
import {DomModule} from '@polymer/polymer/lib/elements/dom-module.js';
import '@polymer/iron-icons/iron-icons.js';
import '@polymer/iron-icons/social-icons.js';
import '@polymer/iron-icon/iron-icon.js';
import '@cbar/cbar-shared-styles';
import '@polymer/paper-checkbox/paper-checkbox.js';

const themeTemplate = DomModule.import('cbar-shared-styles', 'template');
const sharedThemeElement = themeTemplate?.content?.firstElementChild;

if (!sharedThemeElement) {
  console.warn('The theme template or its first element child is missing.');
}

const sharedTheme = sharedThemeElement?.textContent ?? '';

class CbarPlacesItem extends LitElement {
  static get properties() {
    return {
      /**
       * place title
       */
      title: {
        type: String
      },
      /**
       * place image placed in iron-image element (40px/40px)
       */
      image: {
        type: String
      },
      imageShape: {
        type: String,
        reflect: true
      },
      /**
       * description in small text under title
       */
      description: {
        type: String
      },
      extraInformation: {
        type: String
      },
      /**
       * distance metric used to display in the top right of the screen
       */
      meta: {
        type: String
      },
      /**
       * used for identifying the place data externally
       */
      id: {
        type: Number
      },
      imageSizing: {
        type: String
      },
      icon: {
        type: String
      },
      showCheckbox: {
        type: Boolean
      },
      checkboxChecked: {
        type: Boolean
      },
      unclickable: {
        type: Boolean,
        reflect: true
      },
      loading: {
        type: Boolean,
        value: false,
        reflect: true
      }
    }
  }

  constructor() {
    super();
    this.meta = "";
    this.title = "";
    this.image = "";
    this.description = "";
    this.extraInformation = "";
    this.imageShape = "square";
    this.imageSizing = "";
    this.icon = "";
    this.showCheckbox = false;
    this.checkboxChecked = false;
    this.clickable = false;
  }

  /**
   * gets the distance property that gets set in set distance function
   * @return {String}
   */
  get meta() {
    return this._meta;
  }

  /**
   *  returns the title property that gets set in set title function
   * @return {String}
   */
  get title() {
    return this._title;
  }

  /**
   * returns the image property that gets set in the set image function
   * @return {String}
   */
  get image() {
    return this._image;
  }

  get icon() {
    return this._icon;
  }

  get imageShape() {
    return this._imageShape;
  }

  /**
   * returns the description property that gets set in the set description function
   * @return {String}
   */
  get description() {
    return this._description;
  }

  get extraInformation() {
    return this._extraInformation;
  }

  get imageSizing() {
    return this._imageSizing;
  }

  get imageDiv() {
    if(this.loading){
      return html`<div class='loading-image'></div>`;
    }

    return !!this.image ? html`<img src="${this.image}" sizing="${this.imageSizing}" loading="${this.loading}"/>` : this.iconDiv;
  }

  get iconDiv() {
    return !!this.icon ? html`
        <iron-icon icon="${this.icon}"></iron-icon>` : html``;
  }

  get showCheckbox() {
    return this._showCheckbox;
  }

  get checkboxChecked() {
    return this._checkboxChecked;
  }

  get clickable() {
    return this._clickable;
  }

  /**
   * Expected meter value
   * @param distance
   */
  set meta(distance) {
    this._meta = distance;
  }

  /**
   * Set title property value to whatever we pass in, defaults to an empty string
   */
  set title(title) {
    this._title = title ? title : "";
  }

  /**
   * Set image property value to whatever we pass in, defaults to an empty string
   */
  set image(image) {
    this._image = image ? image : "";
  }

  /**
   * Set description property value to whatever we pass in, defaults to an empty string
   */
  set description(description) {
    this._description = description ? description : "";
  }

  set extraInformation(information) {
    this._extraInformation = information;
  }

  set imageSizing(sizing) {
    this._imageSizing = sizing;
  }

  set imageShape(shape) {
    this._imageShape = shape;
  }

  set icon(icon) {
    this._icon = icon;
  }

  set showCheckbox(show) {
    this._showCheckbox = show;
  }

  set checkboxChecked(checked) {
    this._checkboxChecked = checked;
    // this needs to rerender
    this.requestUpdate()
  }

  set clickable(clickable) {
    this._clickable = clickable;
  }

  render() {
    return html`
        <style>
            ${sharedTheme}
            :host {
                font-family: Roboto, noto, sans-serif;
            }

            :host([imageshape='circle']) img {
                border-radius: 50%;
            }

            img[sizing="contain"] {
                object-fit: contain;
            }

            img[sizing="cover"] {
                object-fit: cover;
            }

            :host(:not([unclickable])) .wrapper {
                cursor: pointer;
            }

            :host([loading]) .title-div{
                flex: 1;
                margin-left: 8px;
            }

            :host([loading]) .title{
                height: 16px;
                margin-bottom: 4px;
            }

            .wrapper {
                padding: 16px 16px;
                display: flex;
                justify-content: space-between;
                height: 40px;
                max-height: 40px;
                transition: background-color 0.5s ease;
                background-color: var(--cbar-list-item-color, var(--cbar-sys-color-surface-container, #eeeeee));
            }

            :host([lines="3"]) .wrapper {
                height: 56px;
                max-height: 56px;
            }

            :host(:not([unclickable])) .wrapper:hover {
                background-color: var(--cbar-list-item-hover-color, var(--cbar-sys-color-surface-container-high, #aaaaaa));
            }

            img, iron-icon, .loading-image {
                height: 40px;
                width: 40px;
            }

            iron-icon {
                height: 28px;
                width: 28px;
                min-width: 28px;
            }

            .image-div {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 40px;
                width: 40px;
            }

            .image-div:not(.loading) {
                padding-right: 16px;
            }

            .left-div {
                width: 100%;
                min-width: 0;
                display: flex;
                padding-right: 28px;
                flex-direction: row;
            }

            .right-div {
                display: flex;
                align-self: flex-start;
                /* Fixing shrinking distance div*/
                flex-shrink: 0;
                padding-right: 16px;
                padding-top: 2px;
            }

            .title-div, image-div {
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .title-div {
                max-width: calc(100% - 68px);
            }

            :host([lines="3"]) .left-div {
                align-items: center;
            }

            .location, .title, .extra-data {
                white-space: nowrap;
                word-break: break-all;
                text-overflow: ellipsis;
                overflow: hidden;
            }

            .location, .right-div, .extra-data {
                height: 13px;
                font-size: 12px;
                padding-top: 3px;
                padding-bottom: 3px;
                color: var(--cbar-sys-color-on-surface, var(--cbar-secondary-text, grey));
            }

            .title {
                font-size: 16px;
            }

            h3 {
                margin: 0;
            }
        </style>


        <div class="wrapper" @click="${this.loading ? "" : this.handleSelected}">
            <div class="left-div">
                <div class="image-div ${this.loading ? 'loading' : ''}">
                    ${this.imageDiv}
                </div>
                <div class="title-div">
                    <div class="title ${this.loading ? 'loading' : ''}">
                        ${!this.loading ? this.title : ""}
                    </div>
                    ${this.description ? html`
                        <div class="location ${this.loading ? 'loading' : ''}">${this.loading ? '' : this.description}
                        </div>` : ""}
                    ${this.getAttribute('lines') === "3" && !!this.extraInformation ? html`
                        <div class="extra-data ${this.loading ? 'loading' : ''}">
                            ${this.loading ? '' : this.extraInformation}
                        </div>
                    ` : ""}
                </div>
            </div>
            ${this.meta ? html`
                <div class="right-div ${this.loading ? 'loading' : ''}">
                    <div>${this.loading ? '' : this.meta}</div>
                </div>` : this.showCheckbox ? html`
                <paper-checkbox style="display:flex;align-items:center" @tap="${(e) => {this.handleCheckbox(e)}}" ?checked="${this.loading ? false : this.checkboxChecked}"></paper-checkbox>` : html`
                <slot></slot>`}
        </div>
    `;
  }

  handleSelected(e) {
    if (e.composedPath().includes(this.shadowRoot.querySelector('slot'))) {
      return;
    }

    this.dispatchEvent(new CustomEvent('item-selected', {
      bubbles: true,
      composed: true,
      detail: {
        item: {
          id: this.id,
          title: this.title,
          description: this.description,
          image: this.image
        }
      }
    }));
  }

  handleCheckbox(e) {
    if (this.loading) {
      return;
    }

    this.dispatchEvent(new CustomEvent('checkbox-checked', {
      bubbles: true,
      composed: true,
      detail: {
        item: {
          id: this.id,
          title: this.title,
          description: this.description,
          image: this.image,
          checked: e.target.checked
        }
      }
    }))
  }
}

customElements.define('cbar-places-item', CbarPlacesItem);